import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, CircularProgress, Alert } from "@mui/material";

const ResourceDownloadPage = () => {
  const { resourceId } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Flag to prevent duplicate download requests
    let isDownloadInitiated = false;

    const initiateDownload = async () => {
      if (isDownloadInitiated) return;
      isDownloadInitiated = true;

      try {
        // Get the API base URL from environment variable
        const API_URL =
          process.env.REACT_APP_API_URL || "http://localhost:9000/api";
        const token = localStorage.getItem("token");

        console.log(
          "Initiating resource download for resource ID:",
          resourceId
        );
        console.log("API URL:", API_URL);
        console.log("Token available:", !!token);

        if (!token) {
          throw new Error(
            "Authentication token not found. Please log in again."
          );
        }

        // First, initiate the download
        const response = await fetch(`${API_URL}/resources/start-download`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ resourceId }),
        });

        if (!response.ok) {
          console.error(
            "Download initiation failed:",
            response.status,
            response.statusText
          );
          // Try to get more details from the error response
          const errorData = await response.json().catch(() => ({}));
          console.error("Error details:", errorData);
          throw new Error(`Failed to start download: ${response.statusText}`);
        }

        const data = await response.json();
        console.log("Download response data:", data);

        // Verify we have a valid download URL
        if (!data.downloadUrl) {
          throw new Error("Invalid download URL received");
        }

        // Create a temporary link and click it
        const link = document.createElement("a");
        // Ensure the URL already has the token included
        if (data.downloadUrl.includes("?")) {
          // If URL already has query parameters, ensure token is included
          if (!data.downloadUrl.includes("token=")) {
            link.href = `${data.downloadUrl}&token=${token}`;
          } else {
            link.href = data.downloadUrl;
          }
        } else {
          // If URL has no query parameters, add token as first parameter
          link.href = `${data.downloadUrl}?token=${token}`;
        }

        console.log("Download URL:", link.href);
        link.setAttribute("download", ""); // This will force download
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Navigate back after a short delay
        setTimeout(() => {
          navigate(-1);
        }, 1000);
      } catch (error) {
        console.error("Download error:", error);
        setError("Failed to download resource. Please try again.");
        isDownloadInitiated = false; // Reset flag on error to allow retry
      } finally {
        setLoading(false);
      }
    };

    initiateDownload();

    // Cleanup function
    return () => {
      isDownloadInitiated = true; // Prevent download if component unmounts and remounts
    };
  }, [resourceId, navigate]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography>Downloading resource...</Typography>
    </Box>
  );
};

export default ResourceDownloadPage;
